import { DocumentObject, deserialize } from '@jsonapiworld/jsonapi-serializer';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Institution, InstitutionSource, InstitutionType } from '~/types/institution';
import { MetaDataCursorPage, pagedMetaSchema } from '~/types/jsonapi';
import { isDefined } from '~/types/typeGuards';
import {
  buildJsonAPIQueryParams,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFilterBuilder,
  PaginationInfo,
} from '~/utils/jsonapi-query';
import { backendApiGateway } from '../client/backend';

const InstitutionsPageSize = 20;

const InstitutionsQueryKeys = {
  searchInstitutions: (search: string, type: InstitutionType) => ['Institutions', 'Search', { search, type }],
  institutions: (search: string, type: InstitutionType, source?: InstitutionSource) => [
    'Institutions',
    'Search',
    { search, type, source },
  ],
  single: (institutionId: Institution['id'] | null) => ['Institutions', institutionId],
};

interface InstitutionsMeta {
  page: MetaDataCursorPage;
}

const fetchInstitutions = async (
  {
    search,
    type,
    hidden,
    source,
    excludedSources,
  }: {
    search: string | undefined;
    type: InstitutionType;
    hidden?: boolean;
    source?: InstitutionSource;
    excludedSources?: InstitutionSource[];
  },
  pagination?: PaginationInfo,
) => {
  const filters = jsonAPIFilterBuilder();
  if (search) {
    filters.iLikeAnd('search', search.split(' '));
  }
  filters.contains('types', type);
  if (source) {
    filters.eq('source', source);
  }
  if (excludedSources) {
    filters.notIn('source', excludedSources);
  }
  if (isDefined(hidden)) {
    filters.eq('hidden', hidden);
  }

  const paging = jsonAPICursorPaginatorBuilder({ size: pagination?.size ?? InstitutionsPageSize });

  if (pagination?.cursor) {
    paging.cursor(pagination.cursor);
  }

  const queryParams = buildJsonAPIQueryParams(filters, paging);

  const { data } = await backendApiGateway.get<DocumentObject & { meta: InstitutionsMeta }>('/api/v1/institutions', {
    params: queryParams,
  });

  const { page } = pagedMetaSchema.parse(data.meta);

  return {
    data: deserialize<Institution>(data) as Institution[],
    page,
  };
};

export const useInstitutions = (search: string, type: InstitutionType) => {
  return useQuery({
    queryKey: InstitutionsQueryKeys.institutions(search, type),
    queryFn: async () => {
      const { data } = await fetchInstitutions({ search, type, hidden: false });
      return data;
    },
  });
};

export const useInstitutionsSearch = (
  search: string,
  type: InstitutionType,
  options: {
    enabled: boolean;
  } = { enabled: true },
) => {
  return useInfiniteQuery({
    queryKey: InstitutionsQueryKeys.institutions(search, type),
    queryFn: ({ pageParam }) =>
      fetchInstitutions(
        { search, type, hidden: false },
        {
          size: InstitutionsPageSize,
          cursor: pageParam,
        },
      ),
    initialPageParam: '',
    getNextPageParam: ({ data, page }) => {
      const { cursor } = page;
      if (data.length >= InstitutionsPageSize) {
        return cursor;
      }
      return undefined;
    },
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
};

export const useSwanInstitution = () => {
  const search = '';
  const type = InstitutionType.Banking;
  const source = InstitutionSource.SWAN;
  const { data } = useQuery({
    queryKey: InstitutionsQueryKeys.institutions(search, type, source),
    queryFn: async () => {
      const { data } = await fetchInstitutions({ search, type, source });
      return data;
    },
  });
  return data?.[0];
};

const fetchInstitution = async (institutionId: Institution['id']): Promise<Institution> => {
  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/institutions/${institutionId}`);
  return deserialize<Institution>(data) as Institution;
};

export const useInstitutionQuery = (institutionId: Institution['id'] | null) => {
  return useQuery({
    queryKey: InstitutionsQueryKeys.single(institutionId),
    queryFn: () => fetchInstitution(institutionId ?? ''),
    enabled: !!institutionId,
  });
};
